import type { ReactNode } from "react";

export type Column<T> = {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  width?: string;
  mono?: boolean;
};

/** Optional multi-select for a table. When provided, a leading checkbox column
 *  is rendered; only rows for which `isSelectable` returns true get a checkbox.
 *  The header checkbox toggles all selectable rows. Checkbox clicks are isolated
 *  from `onRowClick` so selecting a row never navigates. */
export type Selection<T> = {
  isSelectable: (row: T) => boolean;
  isSelected: (row: T) => boolean;
  onToggle: (row: T) => void;
  onToggleAll: () => void;
  /** All selectable rows are selected (drives the header checkbox checked state). */
  allSelected: boolean;
  /** Some but not all selectable rows are selected (header indeterminate). */
  someSelected: boolean;
};

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  selection,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  selection?: Selection<T>;
}) {
  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <table className="table">
      <thead>
        <tr>
          {selection ? (
            <th className="table__select-cell">
              <input
                type="checkbox"
                aria-label="Select all re-runnable rows"
                checked={selection.allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = selection.someSelected && !selection.allSelected;
                }}
                onChange={selection.onToggleAll}
              />
            </th>
          ) : null}
          {columns.map((c) => (
            <th key={c.key} style={c.width ? { width: c.width } : undefined}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={rowKey(r)}
            className={onRowClick ? "table__row--clickable" : undefined}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            // Keyboard accessibility: a clickable row must be focusable and
            // respond to Enter/Space, not be mouse-only. We intentionally do NOT
            // set role="button" — that would strip the native row/cell semantics
            // screen readers rely on to navigate the table. A focusable row with
            // an Enter/Space handler keeps both: table semantics + keyboard nav.
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(r);
                    }
                  }
                : undefined
            }
          >
            {selection ? (
              <td
                className="table__select-cell"
                // Stop the click/keydown from bubbling to the row handler so
                // ticking a checkbox never triggers navigation.
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {selection.isSelectable(r) ? (
                  <input
                    type="checkbox"
                    aria-label="Select row"
                    checked={selection.isSelected(r)}
                    onChange={() => selection.onToggle(r)}
                  />
                ) : null}
              </td>
            ) : null}
            {columns.map((c) => (
              <td key={c.key} className={c.mono ? "mono" : undefined}>
                {c.render(r)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
